import {
  Group,
  Text,
  Stack,
  Avatar,
  Badge,
} from '@mantine/core';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'react-feather';
import type { CohortCoachWithDetails } from '../../../../services/api/cohortCoaches';
import type { ColumnConfig } from '../../../../hooks';
import { CoachSatisfactionRating, GroupBadge, renderTextCell } from '../../../../components/common';
import { getAvatarColor, getInitialsFromParts } from '../../../../utils/avatarHelpers';
import { isMedleyEmail } from '../../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../../components/coaches/noMedleyEmailBadge';

/** Get coach full name */
function getCoachName(coach: CohortCoachWithDetails) {
  const name = [coach.first_name, coach.last_name].filter(Boolean).join(' ');
  return name || coach.email;
}

/** Render coach name cell with avatar */
function renderCoachCell(coach: CohortCoachWithDetails) {
  const colorKey = `${coach.first_name || ''}${coach.last_name || ''}`;
  const bgColor = getAvatarColor(colorKey);
  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar
        src={coach.avatar_url}
        size={40}
        radius="xl"
        styles={{
          root: { backgroundColor: bgColor },
          placeholder: { color: '#FFFFFF', backgroundColor: bgColor },
        }}
      >
        {getInitialsFromParts(coach.first_name, coach.last_name, coach.email)}
      </Avatar>
      <Stack gap={0}>
        <Text size="sm" fw={500}>
          {getCoachName(coach)}
        </Text>
        {/* MED-1355: a pool coach can have no Medley email. They need one
            before they can lead a group or be a 1:1 coach, so point the CM
            to the profile, where they can add it. The badge sits on the
            second line, so the name does not wrap. */}
        {!isMedleyEmail(coach.email) && (
          <Badge
            component={Link}
            to={`/coaches/${coach.coach_profile_id}`}
            onClick={(event: MouseEvent) => event.stopPropagation()}
            styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
            style={{ cursor: 'pointer', alignSelf: 'flex-start', margin: '2px 0' }}
            title="Needs a Medley email before they can lead a group or be a 1:1 coach. Open the profile to add one."
          >
            {NO_MEDLEY_EMAIL_LABEL}
          </Badge>
        )}
        {/* MED-1355: specializations are optional now. With none, show no
            second line, not the raw lowercase status. */}
        {coach.coaching_specializations && (
          <Text size="xs" c="dimmed">
            {coach.coaching_specializations}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

/** Render availability badge */
function renderAvailabilityBadge(coach: CohortCoachWithDetails) {
  const config: Record<string, { color: string; text: string; styles?: Record<string, Record<string, string>> }> = {
    available: { color: 'green', text: '100% Available' },
    partially_available: { color: 'yellow', text: `${coach.availability_percent}% Available` },
    pending_response: { color: 'blue', text: 'Pending Response' },
    unavailable: { color: 'red', text: 'Unavailable' },
    not_requested: { color: 'gray', text: 'Not Requested', styles: { root: { border: 'none', color: '#888888' } } },
  };
  const badge = config[coach.availability_status] || config.not_requested;
  return (
    <Badge
      size="sm"
      color={badge.color}
      variant="light"
      tt="none"
      {...(badge.styles ? { styles: badge.styles } : {})}
    >
      {badge.text}
    </Badge>
  );
}

/** Render group assignments */
function renderGroupAssignments(coach: CohortCoachWithDetails) {
  if (!coach.assigned_groups || coach.assigned_groups.length === 0) {
    return <GroupBadge />;
  }
  const displayGroups = coach.assigned_groups.slice(0, 3);
  const extraCount = coach.assigned_groups.length - 3;
  return (
    <Group gap={4} wrap="wrap">
      {displayGroups.map((group) => (
        <GroupBadge key={group.id} groupName={group.name} groupId={group.id} />
      ))}
      {extraCount > 0 && (
        <Text size="xs" c="dimmed">+{extraCount} more</Text>
      )}
    </Group>
  );
}

/** Render session count cell */
function renderSessionCount(count: number) {
  if (count > 0) {
    return (
      <Text size="sm">
        {count}
        <Text span size="xs" c="dimmed"> sessions</Text>
      </Text>
    );
  }
  return <Text size="sm" c="dimmed">&mdash;</Text>;
}

/** Render contract status badge */
function renderContractBadge(coach: CohortCoachWithDetails) {
  // MED-685: partner coaches have no Medley contract — show "N/A" so the column
  // clearly communicates "not applicable" rather than implying a missing action.
  if (coach.coach_type === 'partner') {
    return <Text size="sm" c="dimmed">N/A</Text>;
  }
  if (!coach.assigned_groups || coach.assigned_groups.length === 0) {
    return <Text size="sm" c="dimmed">&mdash;</Text>;
  }
  switch (coach.contract_status) {
    case 'signed':
      return (
        <Badge size="sm" color="medleyGreen" variant="light" tt="none" leftSection={<CheckCircle size={10} />}>
          Completed
        </Badge>
      );
    case 'announced':
      return (
        <Badge size="sm" color="warningAmber" variant="light" tt="none">
          Awaiting
        </Badge>
      );
    default:
      return <Text size="sm" c="dimmed">&mdash;</Text>;
  }
}

/** Render timezone cell */
function renderTimezoneCell(coach: CohortCoachWithDetails) {
  return (
    <Stack gap={0}>
      <Text size="sm">{coach.home_base_timezone || '-'}</Text>
      {coach.confirmed_time_slots.length > 0 && (
        <Text size="xs" c="dimmed">
          +{coach.confirmed_time_slots.length} more
        </Text>
      )}
    </Stack>
  );
}

/** Main cell content renderer for coaches table */
export function renderCoachCellContent(column: ColumnConfig, coach: CohortCoachWithDetails) {
  switch (column.id) {
    case 'coach':
      return renderCoachCell(coach);
    case 'satisfaction':
      return (
        <CoachSatisfactionRating
          rating={coach.satisfaction_score}
          surveyCount={coach.satisfaction_survey_count}
        />
      );
    case 'region':
      return renderTextCell(coach.location);
    case 'timezone':
      return renderTimezoneCell(coach);
    case 'availability':
      return renderAvailabilityBadge(coach);
    case 'groups':
      return renderGroupAssignments(coach);
    case 'one_on_one':
      return renderSessionCount(coach.one_on_one_count);
    case 'workshop':
      return renderSessionCount(coach.workshop_session_count);
    case 'contract':
      return renderContractBadge(coach);
    default:
      return <Text size="sm">&mdash;</Text>;
  }
}
